export const AI_VOICE_CLONING_TASK_TYPE = 'voice-clone'
export const AI_VOICE_CLONING_GENERATION_TYPE = 'voice-clone'
export const AI_VOICE_CLONING_COIN_TYPE = 'gold_coin'
export const AI_VOICE_CLONING_NOISE_TASK_TYPE = 'audio-cleaner'
export const AI_VOICE_CLONING_NOISE_GENERATION_TYPE = 'audio_cleaner'

export const AI_VOICE_CLONING_POLL_INTERVAL_MS = 10_000
export const AI_VOICE_CLONING_PENDING_TASK_PREFIX = 'voice-clone-pending-'

export const VOICE_CLONE_AUDIO_ACCEPT =
  '.mp3,.m4a,.wav,audio/mpeg,audio/mp3,audio/mp4,audio/x-m4a,audio/wav,audio/x-wav'

export const VOICE_CLONE_UPLOAD_LIMITS = {
  minSeconds: 10,
  maxSeconds: 60,
  maxMb: 20,
}

export const VOICE_CLONE_RECORD_LIMITS = {
  minSeconds: 10,
  maxSeconds: 60,
}

export const VOICE_CLONE_PREVIEW_TEXT_LIMITS = {
  minChars: 1,
  maxChars: 100,
}

export const VOICE_CLONE_BILLING = {
  cloneCreditsPer100Chars: 1,
  noiseReductionCreditsPer60s: 1,
  saveVoiceCredits: 0,
}

export const VOICE_CLONE_GENDER_OPTIONS = [
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
  { value: 'other', label: 'Other' },
]

export const VOICE_CLONE_AGE_OPTIONS = [
  { value: 'young', label: 'Young' },
  { value: 'middle-aged', label: 'Middle-aged' },
  { value: 'old', label: 'Old' },
]

export const VOICE_CLONE_LANGUAGE_OPTIONS = [
  { value: 'swedish', label: 'Swedish' },
  { value: 'croatian', label: 'Croatian' },
  { value: 'czech', label: 'Czech' },
  { value: 'danish', label: 'Danish' },
  { value: 'dutch', label: 'Dutch' },
  { value: 'english', label: 'English' },
  { value: 'filipino', label: 'Filipino' },
  { value: 'afrikaans', label: 'Afrikaans' },
  { value: 'arabic', label: 'Arabic' },
  { value: 'bulgarian', label: 'Bulgarian' },
  { value: 'catalan', label: 'Catalan' },
  { value: 'chinese', label: 'Chinese' },
  { value: 'chinese-yue', label: 'Chinese, Yue' },
]

export const VOICE_CLONE_DEFAULT_PREVIEW_TEXT =
  'Welcome to a new way of creating natural, expressive voice content with AI.'

export const VOICE_CLONE_DEMO_AUDIO_BASE =
  'https://static.lip-sync.ai/lipsync/voice-cloning/demo/'

export const VOICE_CLONE_DEMO_ITEMS = [
  {
    id: 'woman-voice',
    name: 'Woman Voice',
    beforeAudioUrl: `${VOICE_CLONE_DEMO_AUDIO_BASE}woman-voice-original.mp3`,
    afterAudioUrl: `${VOICE_CLONE_DEMO_AUDIO_BASE}woman-voice-cloned.mp3`,
  },
  {
    id: 'pikachu-voice',
    name: 'Pikachu Voice',
    beforeAudioUrl: `${VOICE_CLONE_DEMO_AUDIO_BASE}pikachu-voice-original.mp3`,
    afterAudioUrl: `${VOICE_CLONE_DEMO_AUDIO_BASE}pikachu-voice-cloned.mp3`,
  },
  {
    id: 'man-voice',
    name: 'Man Voice',
    beforeAudioUrl: `${VOICE_CLONE_DEMO_AUDIO_BASE}man-voice-original.mp3`,
    afterAudioUrl: `${VOICE_CLONE_DEMO_AUDIO_BASE}man-voice-cloned.mp3`,
  },
  {
    id: 'kid-voice',
    name: 'Kid Voice',
    beforeAudioUrl: `${VOICE_CLONE_DEMO_AUDIO_BASE}kid-voice-original.mp3`,
    afterAudioUrl: `${VOICE_CLONE_DEMO_AUDIO_BASE}kid-voice-cloned.mp3`,
  },
]

const MANUAL_UPLOAD_AUDIO_EXTENSIONS = new Set(['mp3', 'm4a', 'wav'])
const MANUAL_UPLOAD_AUDIO_MIME_TYPES = new Set([
  'audio/mpeg',
  'audio/mp3',
  'audio/mp4',
  'audio/x-m4a',
  'audio/wav',
  'audio/x-wav',
])

export function fileSizeMb(file) {
  return (Number(file?.size) || 0) / 1024 / 1024
}

export function fileExtension(name = '') {
  const base = String(name).split(/[/\\]/).pop() || ''
  const dot = base.lastIndexOf('.')
  return dot >= 0 ? base.slice(dot + 1).toLowerCase() : ''
}

export function isAllowedVoiceCloneAudioFile(file) {
  if (!file) return false
  const ext = fileExtension(file.name)
  const mime = String(file.type || '').toLowerCase()
  return MANUAL_UPLOAD_AUDIO_EXTENSIONS.has(ext) || MANUAL_UPLOAD_AUDIO_MIME_TYPES.has(mime)
}

export function clampVoiceClonePreviewText(text) {
  return String(text || '').slice(0, VOICE_CLONE_PREVIEW_TEXT_LIMITS.maxChars)
}

export function voiceCloneTextLength(text) {
  return Array.from(String(text || '').trim()).length
}

export function voiceCloneCreditsForText(text) {
  const length = voiceCloneTextLength(text)
  if (length <= 0) return 0
  return Math.ceil(length / 100) * VOICE_CLONE_BILLING.cloneCreditsPer100Chars
}

export function voiceCloneNoiseCreditsForDuration(duration) {
  const seconds = Math.max(0, Number(duration) || 0)
  if (seconds <= 0) return 0
  return Math.ceil(seconds / 60) * VOICE_CLONE_BILLING.noiseReductionCreditsPer60s
}

export function voiceCloneEstimatedCredits({ text = '', duration = 0, removeNoise = false } = {}) {
  const textCredits = Math.max(
    VOICE_CLONE_BILLING.cloneCreditsPer100Chars,
    voiceCloneCreditsForText(text),
  )
  const noiseCredits = removeNoise
    ? Math.max(
        VOICE_CLONE_BILLING.noiseReductionCreditsPer60s,
        voiceCloneNoiseCreditsForDuration(duration),
      )
    : 0
  return textCredits + noiseCredits
}

export function parseVoiceCloneParams(params) {
  if (!params) return {}
  if (typeof params === 'string') {
    try {
      const parsed = JSON.parse(params)
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
    } catch {
      return {}
    }
  }
  return typeof params === 'object' && !Array.isArray(params) ? params : {}
}

function resolveAudioUrlCandidate(value) {
  if (Array.isArray(value)) {
    for (const item of value) {
      const resolved = resolveAudioUrlCandidate(item)
      if (resolved) return resolved
    }
    return ''
  }
  if (value && typeof value === 'object') {
    return resolveAudioUrlCandidate(
      value.result ||
        value.audio_url ||
        value.voice_url ||
        value.voiceUrl ||
        value.url ||
        value.output_url ||
        value.after_audio_url ||
        value.preview_audio_url ||
        value.result_audio_url ||
        value.cloned_audio_url,
    )
  }
  const url = String(value || '').trim()
  return url
}

function firstAudioUrlCandidate(...values) {
  for (const value of values) {
    const resolved = resolveAudioUrlCandidate(value)
    if (resolved) return resolved
  }
  return ''
}

export function resolveVoiceCloneBeforeUrl(data = {}) {
  const params = parseVoiceCloneParams(data.params)
  return firstAudioUrlCandidate(
    data.beforeAudioUrl,
    data.before_audio_url,
    data.sourceAudioUrl,
    data.source_audio_url,
    data.original_audio_url,
    data.input_audio_url,
    params.before_audio_url,
    params.sourceAudioUrl,
    params.source_audio_url,
    params.original_audio_url,
    params.input_audio_url,
    params.audio_url,
  )
}

export function resolveVoiceCloneAfterUrl(data = {}) {
  const params = parseVoiceCloneParams(data.params)
  return firstAudioUrlCandidate(
    data.afterAudioUrl,
    data.result,
    data.after_audio_url,
    data.result_audio_url,
    data.cloned_audio_url,
    data.preview_audio_url,
    data.voice_url,
    data.voiceUrl,
    data.audio_url,
    data.output_url,
    data.url,
    params.afterAudioUrl,
    params.result,
    params.after_audio_url,
    params.result_audio_url,
    params.cloned_audio_url,
    params.preview_audio_url,
    params.voice_url,
    params.voiceUrl,
    params.audio_url,
    params.output_url,
    params.url,
  )
}

export function buildVoiceCloneCreatePayload(data = {}) {
  const audioUrl = String(data.audio_url || '').trim()
  const sourceAudioUrl = String(
    data.source_audio_url ||
      data.before_audio_url ||
      data.original_audio_url ||
      audioUrl,
  ).trim()
  const text = String(data.text || data.preview_text || '').trim()
  return {
    task_type: data.task_type || AI_VOICE_CLONING_TASK_TYPE,
    generation_type: data.generation_type || AI_VOICE_CLONING_GENERATION_TYPE,
    audio_url: audioUrl,
    source_audio_url: sourceAudioUrl,
    before_audio_url: data.before_audio_url || sourceAudioUrl,
    original_audio_url: data.original_audio_url || sourceAudioUrl,
    cleaned_audio_url: data.cleaned_audio_url || '',
    text,
    preview_text: text,
    language: data.language || data.preview_language || '',
    preview_language: data.preview_language || data.language || '',
    duration: Number(data.duration) || 0,
    remove_background_noise: Boolean(data.remove_background_noise),
    noise_reduction: Boolean(data.noise_reduction ?? data.remove_background_noise),
    estimated_credits: Number(data.estimated_credits) || 0,
    coin_type: data.coin_type || AI_VOICE_CLONING_COIN_TYPE,
  }
}

export function buildVoiceCloneCreateTaskRequestPayload(data = {}) {
  return {
    generation_type: data.generation_type || AI_VOICE_CLONING_GENERATION_TYPE,
    coin_type: data.coin_type || AI_VOICE_CLONING_COIN_TYPE,
    task_type: data.task_type || AI_VOICE_CLONING_TASK_TYPE,
    audio_url: String(data.audio_url || '').trim(),
    text: String(data.text || data.preview_text || '').trim(),
  }
}

export function buildVoiceCloneNoiseReductionPayload(data = {}) {
  return {
    generation_type: data.generation_type || AI_VOICE_CLONING_NOISE_GENERATION_TYPE,
    coin_type: data.coin_type || AI_VOICE_CLONING_COIN_TYPE,
    task_type: data.task_type || AI_VOICE_CLONING_NOISE_TASK_TYPE,
    audio_url: String(data.audio_url || '').trim(),
    duration: Math.ceil(Number(data.duration) || 0),
  }
}
